<template>
  <div class="flex flex-col gap-4">
    <div class="px-4">
      <h4 class="text-md font-semibold break-all line-clamp-2">{{ data.name }}</h4>
    </div>
    <div class="w-full my-4 z-0">
      <V3ProductImageCarouselComponent :items="images" source="external" class="" type="image" />
    </div>
    <UDivider class="py-0" />
    <div class="px-4">
      <div class="inline-flex justify-between items-center w-full">
        <div class="flex flex-col gap-1">
          <div class="inline-flex justify-center items-center" v-if="discountValue > 0">
            <span class="px-1 text-sm text-gray-500 line-through">&#8377; {{ parseFloat(pricing.maxPrice).toFixed(2)
              }}</span>
            <span class="px-1 text-sm text-success-600 font-semibold">{{ discountPercentage }}% off</span>
          </div>
          <p class="text-md font-semibold" v-if="pricing.price > 0">&#8377; {{ parseFloat(pricing.price).toFixed(2) }}
          </p>
        </div>
        <div class=" flex" v-if="cartItem">
          <div class="w-24"  v-if="!isOutOfStock">
            <V3CartButtonComponent :product="data" :disabled="isOutOfStock" :customize="isCustomizationAvailable" />
          </div>
          <div class="border border-red-200 bg-gray-100 rounded-full p-2 text-center" v-else>
            <p class="text-red-500">Out of Stock</p>
          </div>
        </div>
      </div>
    </div>
    <UDivider class="py-0" v-if="pricing.price > 0" />
    <div class="inline-flex text-md px-4 gap-3 text-center z-[-10]">
      <div v-for="(item, index) in mandates" :key="index">
        <div class="inline-flex items-center gap-1">
          <UIcon :name="item.icon" class="font-semibold" :class="item.class"></UIcon>
          <span>{{ item.title }}</span>
        </div>
      </div>
    </div>
    <div class="inline-flex text-md px-4 gap-1 text-center items-center" v-if="isFandB">
      <div class="inline-flex items-center gap-1" v-if="isVeg">
        <V3ImageComponent src="icons/veg.png" class="h-7 w-7" />
        <p>Veg</p>
      </div>
      <div class="inline-flex items-center gap-1" v-if="isEgg">
        <V3ImageComponent src="icons/egg.png" class="h-7 w-7" />
        <p>Egg</p>
      </div>
      <div class="inline-flex items-center gap-1" v-if="isNonVeg">
        <V3ImageComponent src="icons/nonveg.png" class="h-7 w-7" />
        <p>Non Veg</p>
      </div>
    </div>
    <UDivider class="py-0" />
    <div class="flex flex-col gap-4 px-4" v-if="hasVariants">
      <div class="flex flex-col gap-2" v-for="variant in data.variants[0].variants" :key="variant.label">
        <h6 class="text-md font-bold text-gray-900 pb-1 dark:text-white">
          {{ useStartCase(variant.label == 'uom' ? 'Pack Size' : variant.label) }}
        </h6>
        <div class="flex flex-wrap gap-2">
          <!-- Normal Variant Buttons -->
          <UButton v-if="variant.label !== 'size_chart' && variant.label.toLowerCase() !== 'size chart'"
            v-for="value in variant.values" :key="value" :class="{
              'bg-primary dark:text-white text-white pointer-events-none': currentVariant[variant.label] === value,
              'hover:bg-gray-100 ' : currentVariant[variant.label] !== value
            }" :style="getButtonStyle(variant, value, variant.label)"
            @click="selectVariant(variant, value, variant.label)" :disabled="variantLength > 1 && isDisabled"
            color="primary" variant="outline" class="px-2 py-1 text-xs">

            <span v-if="variant.label !== 'colour'">{{ value }}</span>
          </UButton>

          <!-- Size Chart Button -->
          <UButton v-if="variant.label === 'size_chart' || variant.label.toLowerCase() === 'size chart'" :class="{
            'hover:bg-gray-100': currentVariant[variant.label] !== value
          }" :style="getButtonStyle(variant, value, variant.label)"
            @click="selectVariant(variant, value, variant.label)" :disabled="variantLength > 1 && isDisabled"
            color="primary" variant="outline" class="px-2 py-1 text-xs">
            <span class="text-blue-600 cursor-pointer underline" @click="showPopup(variant.values, index)">Click
              here</span>
          </UButton>
        </div>
      </div>
    </div>
    <UDivider v-if="hasVariants" />
    <div class="px-4 gap-4" v-if="!provider.is_opened">
      <div class="w-full bg-red-700 text-white text-center my-2 px-2 py-1 text-xs">The store is closed <span
          v-if="provider.opens_at">and will reopen at {{ provider.opens_at }}</span></div>
    </div>
    <UDivider v-if="!provider.is_opened" />
    <!-- <UDivider/> -->
    <div class="px-4">
      <UAccordion :items="[{ label: 'Product Details', slot: 'description', defaultOpen: true }]">
        <template #default="{ item, index, open }">
          <UButton color="white" variant="ghost" block class="px-0">
            <template #leading>
              <h5 class="text-md font-semibold">Product Details</h5>
            </template>
            <template #trailing>
              <UIcon name="i-heroicons-chevron-right-20-solid"
                class="w-5 h-5 ms-auto transform transition-transform duration-200 z-[-10]" :class="[open && 'rotate-90']" />
            </template>
          </UButton>
        </template>
        <template #description>
          <div v-html="data.detail.description" class="break-words" />
        </template>
      </UAccordion>
    </div>
    <UDivider />
    <div class="px-4" v-if="!isFandB">
      <div class="flex flex-row flex-wrap">
        <div class="py-3 basis-1/2" v-for="(item, index) in attributes" :key="index">
          <p class="text-sm text-gray-500">{{ item.title }}</p>
          <p class="line-clamp-2" v-html="item.value"></p>
        </div>
      </div>
    </div>
    <!-- <UDivider v-if="attributes.length > 0"/> -->
    <div class="px-4" v-if="!isFandB">
      <div class="flex flex-row flex-wrap">
        <div class="py-3" :class="index === 0 ? 'basis-full' : 'basis-1/2'" v-for="(item, index) in metadata"
          :key="index">
          <p class="text-sm text-gray-500">{{ item.title }}</p>
          <p class="line-clamp-2">{{ item.value }}</p>
        </div>
      </div>
    </div>
    <UDivider class="py-0" v-if="!isFandB" />
    <div class="px-5" :class="{ 'pointer-events-none opacity-50': !provider.is_opened }">
      <h5 class="text-md font-semibold uppercase mb-4">Provider Detail</h5>
      <NuxtLink :to="{ path: `/providers/${provider.ukid}` }">
        <V3StoreCardComponent :data="provider" :key="provider.ukid" />
      </NuxtLink>
    </div>
    <V3ImageLightboxComponent ref="lightbox" :data="sizeChartImages" :image_value="currentImageUrl" />
  </div>
</template>

<script setup>
const props = defineProps(['data']);
const { gtag } = useGtag();
const emit = defineEmits(['storeClose']);
const auth = useAuth();
  const isAuthenticated = auth && auth.access_token;
  const userData = isAuthenticated ? {
    user_name: auth.user.first_name,
    user_phone: auth.user.phone,
    user_email: auth.user.email,
  } : {
    user_name: null,
    user_phone: null,
    user_email: null
  };
const runtimeConfig = useRuntimeConfig();
const isDisabled = ref(false);
const variantLength = ref(0);
const gallery = props.data.images.gallery || [];
const images = gallery
  .map(item => ({ url: item && item !== '' ? item : undefined }))
  .filter(item => item.url);
if (!images || images.length === 0) {
  images.push({ url: runtimeConfig.public.defaultImage });
}
const discountValue = props.data.max_price - props.data.price;
const discountPercentage = Math.floor((discountValue / props.data.max_price) * 100);
const isFandB = props.data.category.parent.name.toUpperCase() === 'F&B';
const isVeg = isFandB && props.data.food_type && props.data.food_type.toLowerCase() === 'veg';
const isEgg = isFandB && props.data.food_type && props.data.food_type.toLowerCase() === 'egg';
const isNonVeg = isFandB && ['nonveg', 'non_veg'].includes(props.data.food_type?.toLowerCase());
const pricing = usePriceBuilder(props.data);
const isOutOfStock = props.data.stock ? Number(props.data.stock.available) === 0 : false;

const hasVariants = props.data.variants && Array.isArray(props.data.variants) && props.data.variants.length > 0 && props.data.variants[0].variants && props.data.variants[0].variants.length > 0;
const isCustomizationAvailable = computed(() => (props.data.customizations && props.data.customizations.customizations && props.data.customizations.customizations.length > 0) ? true : false);

const { data: providerItem } = await useAPIHandler('get', `/providers/${props.data.provider.ukid}`, {
    query: { partner_id: useRuntimeConfig().public.partnerCode },
  });
const provider = { ...providerItem, ...useTimingCheck(providerItem.availability, providerItem.locations) };
if(provider.locations[0].time.label.toLowerCase() === 'close') {
  emit('storeClose')
}

const dayjs = useDayjs();
const isCancellable = props.data.is_cancellable;
const isReturnable = props.data.is_returnable;
const returnWindow = ref(null);
if (props.data.return_window) {
  const days = dayjs.duration(props.data.return_window).asDays();
  const hours = dayjs.duration(props.data.return_window).asHours();
  const minutes = dayjs.duration(props.data.return_window).asMinutes();
  if (days > 1) { returnWindow.value = `${days} Days` }
  else if (hours > 1) { returnWindow.value = `${hours} Hrs` }
  else if (minutes > 1) { returnWindow.value = `${minutes} Mins` }
  else { returnWindow.value = '' }
}

const currentVariant = computed(() => {
  return props.data.variants[0].products.find(item => item.sku_id === props.data.sku_id);
});

const getButtonStyle = (variant, value, label) => {
  const isSelected = currentVariant.value[variant.label] === value;
  let isHighlighted = false;

  const foundVariant = props.data.variants[0].products.filter(product => product[variant.label] === value);
  const excludedKeys = ['product_id', 'sku_id', label];
  const variantKeys = Object.keys(currentVariant.value).filter(key => !excludedKeys.includes(key));

  for (const key of variantKeys) {
    const valueToCheck = currentVariant.value[key];
    const matchingVariant = foundVariant.find(product => product[key] === valueToCheck);

    if (matchingVariant) {
      isHighlighted = true;
      break;
    }
  }

  // isDisabled.value = !(isSelected || isHighlighted);
  // variantLength.value = props.data.variants[0].variants.length;

  return {
    backgroundColor: variant.label === 'colour' ? value : '',
    minWidth: variant.label === 'colour' ? '35px' : '150px',
    minHeight: '35px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: variant.label === 'colour' ? '35px' : '150px', 
    borderRadius: variant.label === 'colour' ? '50%' : '4px',
    width: variant.label === 'colour' ? '45px' : '',
    height: variant.label === 'colour' ? '35px' : '',
    paddingLeft: '20px',
    paddingRight: '10px',
  };
}

const selectVariant = (data, value, label) => {
  const foundVariant = props.data.variants[0].products.filter(product => product[data.label] === value);
  const excludedKeys = ['product_id', 'sku_id', label];
  const variantKeys = Object.keys(currentVariant.value).filter(key => !excludedKeys.includes(key));
  for (const key of variantKeys) {
    const valueToCheck = currentVariant.value[key];
    const matchingVariant = foundVariant.find(product => product[key] === valueToCheck);
    if (matchingVariant) {
      gtag('event', 'cta_event', {
        action: 'click',
        cta_name: label,
        label_data: value,
        label_sku: matchingVariant.sku_id,
        ...userData,
      });
      navigateTo({ path: `/products/${matchingVariant.sku_id}` }, { replace: true })
      return;
    }
  }

  if (foundVariant && foundVariant.length > 0) {
    gtag('event', 'cta_event', {
        action: 'click',
        cta_name: label,
        label_data: value,
        label_sku: foundVariant[0].sku_id,
        ...userData,
      });
    navigateTo({ path: `/products/${foundVariant[0].sku_id}` }, { replace: true })
  }
  
}

const mandates = [
  {
    title: isCancellable ? 'Cancellable' : 'Non-Cancellable',
    icon: isCancellable ? 'i-heroicons-check-circle-16-solid' : 'i-heroicons-x-circle-16-solid',
    class: isCancellable ? 'text-success-900' : 'text-danger-900'
  },
  {
    title: isReturnable ? `Returnable in ${returnWindow.value}` : 'Non-Returnable',
    icon: isReturnable ? 'i-heroicons-arrow-uturn-left-16-solid' : 'i-heroicons-x-circle-16-solid',
    class: isReturnable ? 'text-success-900' : 'text-danger-900',
  }
];

const attributes = [];
const sizeChart = ref(null);
if (props.data.attributes) {
  for (const item of props.data.attributes) {
    const blacklist = ['colour', 'size_chart'];
    if (!blacklist.includes(item.code.toLowerCase())) {
      attributes.push({ title: useStartCase(item.code), value: useStartCase(item.value) })
    }
    if (item.code.toLowerCase() === 'size_chart') {
      images.push({ url: item.value });
      sizeChart.value = item.value;
    }
    if (item.code.toLowerCase() === 'colour') {
      attributes.push(
        { title: 'Color', value: `<div class="w-10 h-5 py-2 border" style="background-color: ${item.value}"></div>` }
      )
    }
  }
}

const manufacturer = props.data.manufacturer;
const metadata = [
  { title: 'Manufacturer Address', value: manufacturer.address || 'NA' },
  { title: 'Manufacturer Name', value: manufacturer.name || 'NA' },
  { title: 'Manufactured Month/Year', value: props.data.detail.manufactured_date || 'NA' },
  { title: 'Country of Origin', value: 'IND' },
  { title: 'Net Quantity', value: props.data.detail.net_quantity || 'NA' }
];

const cart = useCart();
const cartItem = computed(() => cart.items.find(item => item.sku_id === props.data.sku_id));

const currentImageUrl = ref('');
const lightbox = ref(null);
const sizeChartImages = ref([]);
const showPopup = async (imageValue) => {
  sizeChartImages.value = imageValue.map(item => ({ url: item }));
  currentImageUrl.value = imageValue[0];
  if (lightbox.value) {
    lightbox.value.show(0);
  }
}
</script>
